import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  CssBaseline,
} from '@mui/material';
import theme from './theme';
import { setNavigate } from './services/navigationService';
import HomePage from './pages/HomePage';
import CompaniesPage from './pages/CompaniesPage';
import { CompanyHomePage } from './pages/company';
import { ProductPage, ProductDetailPage, ProductAvailabilityComparePage, ProductImportPage } from './pages/product';
import { StockPage, StockDetailPage } from './pages/stock';
import { StockAuditPage, StockAuditDetailPage, CreateStockAuditPage } from './pages/stock-audit';
import ContactPage, { ContactImportPage } from './pages/contact';
import OrganizationPage from './pages/organization';
import { ActivityPage, CreateActivityPage } from './pages/activity';
import EditActivityGroupPage from './pages/activity/create_activity/EditActivityGroupPage';
import CreateShipmentPage from './pages/activity/create_shipment';
import { ActivityTypePage } from './pages/activity-type';
import { CatalogPage, CatalogEditorPage } from './pages/catalog';
import {
  ReportsHomePage,
  NetCashFlowReportPage,
  NetCashFlowByProductReportPage,
  MarginReportPage,
  MarginByProductReportPage,
  InventoryValueReportPage,
} from './pages/report';
import { DebtPage, DebtLedgerPage } from './pages/debt';
import TeamPage from './pages/company/team';
import SettingsPage from './pages/company/settings';
import TelegramPage from './pages/telegram/TelegramPage';
import SerialNumberPage from './pages/SerialNumberPage';
import LoginPage from './pages/LoginPage';
import InvitePage from './pages/InvitePage';
import AppUserMenu from './pages/common/AppUserMenu';
import ReturnToRedirect from './pages/common/ReturnToRedirect';
import RequireAuth from './pages/common/RequireAuth';
import { CatalogViewPage } from './pages/catalog';
import { PreOrderPage, PreOrderDetailPage, ConvertPreOrderPage, MyPreOrdersPage, MyPreOrderDetailPage } from './pages/preorder';
import './App.css';

function NavigationSetter() {
  const nav = useNavigate();
  useEffect(() => { setNavigate(nav); }, [nav]);
  return null;
}

function AppLayout() {
  const location = useLocation();
  const showMenu = location.pathname !== '/login';

  // React Router doesn't reset scroll on client-side navigation, so a scroll
  // position left over from a previous page (e.g. a scrolled-down form) would
  // otherwise carry over and hide content behind the sticky headers.
  // useLayoutEffect (not useEffect) so this runs before the browser paints the
  // new route — on iOS Safari, a tap landing right as navigation happens can
  // otherwise get hit-tested against the new page while it's still visually at
  // the old scroll offset, making the tap miss the sticky header's back button.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {showMenu && <AppUserMenu />}
      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/" element={<RequireAuth><ReturnToRedirect><HomePage /></ReturnToRedirect></RequireAuth>} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/catalogs/:catalogId/view" element={<CatalogViewPage />} />
          <Route path="/my-preorders" element={<MyPreOrdersPage />} />
          <Route path="/my-preorders/:preOrderId" element={<MyPreOrderDetailPage />} />
          <Route path="/companies/:id/home" element={<CompanyHomePage />} />
          <Route path="/companies/:id/products" element={<ProductPage />} />
          <Route path="/companies/:id/products/compare" element={<ProductAvailabilityComparePage />} />
          <Route path="/companies/:id/products/import" element={<ProductImportPage />} />
          <Route path="/companies/:id/products/:productId" element={<ProductDetailPage />} />
          <Route path="/companies/:id/stocks" element={<StockPage />} />
          <Route path="/companies/:id/stocks/:stockId" element={<StockDetailPage />} />
          <Route path="/companies/:id/stock-audits" element={<StockAuditPage />} />
          <Route path="/companies/:id/stock-audits/new" element={<CreateStockAuditPage />} />
          <Route path="/companies/:id/stock-audits/:auditId" element={<StockAuditDetailPage />} />
          <Route path="/companies/:id/contacts" element={<ContactPage />} />
          <Route path="/companies/:id/contacts/import" element={<ContactImportPage />} />
          <Route path="/companies/:id/organizations" element={<OrganizationPage />} />
          <Route path="/companies/:id/activity-types" element={<ActivityTypePage />} />
          <Route path="/companies/:id/catalogs" element={<CatalogPage />} />
          <Route path="/companies/:id/catalogs/create" element={<CatalogEditorPage />} />
          <Route path="/companies/:id/catalogs/:catalogId/edit" element={<CatalogEditorPage />} />
          <Route path="/companies/:id/preorders" element={<PreOrderPage />} />
          <Route path="/companies/:id/preorders/:preOrderId" element={<PreOrderDetailPage />} />
          <Route path="/companies/:id/preorders/:preOrderId/convert" element={<ConvertPreOrderPage />} />
          <Route path="/companies/:id/activities" element={<ActivityPage />} />
          <Route path="/companies/:id/activities/create" element={<CreateActivityPage />} />
          <Route path="/companies/:id/activities/group/:groupId/edit" element={<EditActivityGroupPage />} />
          <Route path="/companies/:id/activities/group/:groupId/shipment" element={<CreateShipmentPage />} />
          <Route path="/companies/:id/reports" element={<ReportsHomePage />} />
          <Route path="/companies/:id/reports/net-cash-flow" element={<NetCashFlowReportPage />} />
          <Route path="/companies/:id/reports/net-cash-flow-by-product" element={<NetCashFlowByProductReportPage />} />
          <Route path="/companies/:id/reports/margin" element={<MarginReportPage />} />
          <Route path="/companies/:id/reports/margin-by-product" element={<MarginByProductReportPage />} />
          <Route path="/companies/:id/reports/inventory-value" element={<InventoryValueReportPage />} />
          <Route path="/companies/:id/debts" element={<DebtPage />} />
          <Route path="/companies/:id/debts/contacts/:contactId" element={<DebtLedgerPage />} />
          <Route path="/activities/:activityId/product-serial-numbers" element={<SerialNumberPage />} />
          <Route path="/companies/:id/team" element={<TeamPage />} />
          <Route path="/companies/:id/settings" element={<SettingsPage />} />
          <Route path="/telegram" element={<TelegramPage />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <NavigationSetter />
        <AppLayout />
      </Router>
    </ThemeProvider>
  );
}

export default App;
